import React, { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import DefaultFallback from '@/_boundaries/DefaultFallback/DefaultFallback';
import PageLoadingSpinner from '@/_components/PageLoadingSpinner/PageLoadingSpinner';

import LanguagePicker from '@/Login/LanguagePicker';
import { LoginFormRenderer } from '@/Login/components/LoginForm/LoginFormRenderer';

import { customIdFieldAtom, loginCodeAtom, loginRequestAtom, loginStatusAtom } from '@/_atoms/auth';
import { languageIdAtom } from '@/_atoms/language';
import { loginActionAtom } from '@/_atoms/loginAction';
import { useLoginRedirect } from '@/_effects/useLoginRedirect';
import { checkForErrorMessage, deriveLoginStatusCode } from '@/_helpers/loginHelpers';
import { getCookie, retrieveSessionFromStorage } from '@/_helpers/queryInterceptorHelpers';

import type { CustomIdField, LoginCode, LoginRequest, LoginStatus, LoginStatusCode, Session } from '@/types/auth';

/**
 * Page component responsible for rendering login-related flows before the respondent receives a valid JWT
 * authentication token.
 */
const LoginPage = (): React.ReactElement => {
  useLoginRedirect();
  const customIdField: CustomIdField = useAtomValue(customIdFieldAtom);
  const loginCode: LoginCode = useAtomValue(loginCodeAtom);
  const [loginStatus, setLoginStatus] = useAtom(loginStatusAtom);
  const [loginRequest, setLoginRequest] = useAtom(loginRequestAtom);
  const languageId: string | null = useAtomValue(languageIdAtom);
  const login = useSetAtom(loginActionAtom);
  const shouldPickLanguage: boolean = loginCode?.languages?.length > 1 && !languageId;

  const session: Session | null = React.useMemo((): Session | null => retrieveSessionFromStorage(), []);
  const cookieLoginId: string = React.useMemo((): string => getCookie('loginid'), []);

  const hasError: boolean = checkForErrorMessage(loginStatus);

  // Ensure the search params are captured only once on initial render
  const initialSearch: string = React.useRef(useLocation().search).current;
  const loginCodeFromUrl: string | null = React.useMemo(
    (): string | null => new URLSearchParams(initialSearch).get('loginCode'),
    [initialSearch]
  );

  const status: LoginStatusCode = deriveLoginStatusCode(
    loginCode?.id,
    loginStatus.previousStatusCodes,
    loginStatus.currentStatusCode,
    !!session?.id,
    !!cookieLoginId,
    loginCodeFromUrl
  );

  /**
   * Handle input changes:
   * - Update the LoginRequest atom with the new field value.
   * - Clear any visible error message in the loginStatus atom.
   */
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setLoginRequest((prev: LoginRequest): LoginRequest => ({ ...prev, [event.target.name]: event.target.value} ));
      setLoginStatus((prev: LoginStatus): LoginStatus => ({ ...prev, errorMessage: '' }));
    },
    [setLoginRequest, setLoginStatus]
  );

  /** Pressing the button triggers a login attempt. */
  const handleSubmit: () => void = useCallback((): void => void login(null), [login]);

  // If multiple languages are available and none is selected, show the language picker
  if (shouldPickLanguage) return <LanguagePicker />

  // Render the appropriate login form or spinner based on the current login status
  return (
    <ErrorBoundary FallbackComponent={DefaultFallback}>
      <React.Suspense fallback={<PageLoadingSpinner label="Logging in" />}>
        <LoginFormRenderer
          currentStatusCode={status}
          customIdField={customIdField ?? loginCode?.customIdField ?? null}
          hasError={hasError}
          loginRequest={loginRequest}
          onChange={handleChange}
          onSubmit={handleSubmit}
        />
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default LoginPage;